import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useLocation, useNavigationType } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { effectivePrefs, resolveSizeParams, hasAnySizes } from "@/lib/sizePrefs";
import ProductCard from "@/components/ProductCard";
import CategoryName from "@/components/CategoryName";
import { useSeo } from "@/lib/seo";
import RecentlyViewed from "@/components/RecentlyViewed";
import BrandFollowButton from "@/components/BrandFollowButton";
import Breadcrumb from "@/components/Breadcrumb";
import { ShieldCheck, Tag, Search, X, ChevronLeft, ChevronRight, BookmarkPlus, Footprints, Ruler, Crown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { GENDERS, AGES, PRODUCT_TYPES, COLORS, PATTERNS, STYLES, SIZES, MATERIALS, ERAS, ERA_LABELS, FABRIC_TYPES, FABRIC_TYPE_LABELS, SHOE_AUDIENCES, SHOE_AUDIENCE_LABELS, SHOE_SIZES, DEPT_LABELS, GARMENTS, CLOTHING_SIZES, garmentLabel, WAIST_SIZES, INSEAM_SIZES, usesWaistLength, kidsGarments, KID_GENDER_LABELS, ACCESSORY_SIZES, WATCH_BANDS, WATCH_BAND_LABELS, WATCH_MOVEMENTS, WATCH_MOVEMENT_LABELS, WATCH_DIAL_COLORS, WATCH_WATER_RESISTANCE, JEWELRY_TYPES, JEWELRY_METALS, JEWELRY_METAL_LABELS, JEWELRY_STONES, JEWELRY_STONE_LABELS, JEWELRY_HALLMARKS, JEWELRY_HALLMARK_LABELS, WALLET_STYLES, WALLET_STYLE_LABELS, WALLET_CARD_SLOTS, HANDBAG_STYLES, HANDBAG_STYLE_LABELS, HANDBAG_SIZES, HANDBAG_SIZE_LABELS, TIE_STYLES, TIE_STYLE_LABELS, HOME_DECOR_CATEGORIES, HOME_DECOR_CATEGORY_LABELS, HOME_DECOR_ITEMS, HOME_DECOR_ITEM_LABELS } from "@/lib/taxonomy";

const PAGE_SIZE = 20;

// On a fresh document load/reload, drop stale saved scroll positions so Browse opens at the top.
// (SPA back navigation keeps them, so hitting Back still restores your spot.)
try {
  Object.keys(sessionStorage).forEach((k) => { if (k.startsWith("browse-scroll:")) sessionStorage.removeItem(k); });
} catch (e) { /* ignore */ }

const PRICE_MAX = 500;
const PRICE_PRESETS = [
  { key: "under25", label: "Under $25", min: null, max: 24 },
  { key: "25to50", label: "$25–50", min: 25, max: 50 },
  { key: "50plus", label: "$50+", min: 50, max: null },
];

function FilterRow({ label, options, value, onChange, testidPrefix, labels }) {
  return (
    <div>
      <div className="label-eyebrow mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        <button className="chip" data-active={!value} onClick={() => onChange(null)} data-testid={`${testidPrefix}-all`}>All</button>
        {options.map((o) => (
          <button key={o} className="chip" data-active={value === o} onClick={() => onChange(o)}
            data-testid={`${testidPrefix}-${o}`}>{labels ? labels[o] : o}</button>
        ))}
      </div>
    </div>
  );
}

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");
  const [demoTotal, setDemoTotal] = useState(0);
  const [fabW, setFabW] = useState("");
  const [fabL, setFabL] = useState("");
  const [patW, setPatW] = useState(params.get("pattern_width") || "");
  const [patL, setPatL] = useState(params.get("pattern_length") || "");

  // Keep scroll position: save as the shopper scrolls, restore only on Back (POP) to the same query.
  const location = useLocation();
  const navType = useNavigationType();
  const scrollKey = `browse-scroll:${location.search}`;
  useEffect(() => {
    const save = () => sessionStorage.setItem(scrollKey, String(window.scrollY));
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [scrollKey]);
  useEffect(() => {
    if (navType !== "POP" || loading || !items.length) return;
    const saved = sessionStorage.getItem(scrollKey);
    if (saved) window.scrollTo(0, parseInt(saved, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType, loading, items.length]);
  useEffect(() => {
    // Remember the exact filtered view so the product breadcrumb can link back to it.
    try { sessionStorage.setItem("ycs_last_browse", location.pathname + location.search); } catch (e) { /* ignore */ }
  }, [location.pathname, location.search]);

  const filters = {
    gender: params.get("gender"),
    age_group: params.get("age_group"),
    product_type: params.get("product_type"),
    color: params.get("color"),
    pattern: params.get("pattern"),
    style: params.get("style"),
    size: params.get("size"),
    material: params.get("material"),
    condition: params.get("condition"),
    era: params.get("era"),
    brand: params.get("brand"),
    seller_id: params.get("seller_id"),
    category: params.get("category"),
    q: params.get("q"),
    min_price: params.get("min_price"),
    max_price: params.get("max_price"),
    audience: params.get("audience"),
    dept: params.get("dept"),
    garment_type: params.get("garment_type"),
    waist: params.get("waist"),
    inseam: params.get("inseam"),
    kid_gender: params.get("kid_gender"),
    difficulty: params.get("difficulty"),
    pattern_width: params.get("pattern_width"),
    pattern_length: params.get("pattern_length"),
    band: params.get("band"),
    movement: params.get("movement"),
    dial_color: params.get("dial_color"),
    metal: params.get("metal"),
    stone: params.get("stone"),
    water_resistance: params.get("water_resistance"),
    resizable: params.get("resizable"),
    hallmark: params.get("hallmark"),
    card_slots: params.get("card_slots"),
  };
  const isFabric = filters.category === "fabric_archives";
  const isHomeDecor = filters.category === "home_decor";
  const isPattern = isFabric && filters.product_type === "pattern";
  const isShoes = filters.product_type === "shoes";
  const audience = filters.audience;
  const dept = filters.dept;
  const isApparelDept = filters.product_type === "apparel" && ["women", "men", "kids"].includes(dept);
  const isGuided = isShoes || isApparelDept;
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const SIZELESS = ["handbags", "wallets", "ties", "earrings"];
  const isWatches = filters.product_type === "watches";
  const isHats = filters.product_type === "hats";
  const isNecklaces = filters.product_type === "necklaces";
  const isWallets = filters.product_type === "wallets";
  const isTies = filters.product_type === "ties";
  const isJewelry = JEWELRY_TYPES.includes(filters.product_type);
  const isHandbags = filters.product_type === "handbags";
  const deptClothingLabel = dept === "kids" ? "Kids' Clothing" : `${DEPT_LABELS[dept] || ""}'s Clothing`;

  const { user } = useAuth();
  const savedAnySizes = hasAnySizes(effectivePrefs(user));
  const [nudgeOff, setNudgeOff] = useState(() => {
    try { return localStorage.getItem("ycs_size_nudge_dismissed") === "1"; } catch { return false; }
  });
  const dismissNudge = () => {
    try { localStorage.setItem("ycs_size_nudge_dismissed", "1"); } catch {}
    setNudgeOff(true);
  };
  const showNudge = isGuided && !savedAnySizes && !nudgeOff;
  const sizeCtx = isShoes && audience ? { type: "shoes", audience }
    : isApparelDept && filters.garment_type ? { type: "apparel", dept, garment: filters.garment_type }
    : null;
  const savedParams = resolveSizeParams(effectivePrefs(user), sizeCtx);
  const savedHas = Object.values(savedParams).some(Boolean);
  const savedActive = savedHas && Object.entries(savedParams).every(([k, v]) => !v || params.get(k) === v);
  const savedLabel = savedParams.size
    || [savedParams.waist && `W${savedParams.waist}`, savedParams.inseam && `L${savedParams.inseam}`].filter(Boolean).join(" ");
  const autoAppliedRef = useRef(new Set());
  const applySaved = () => {
    const p = new URLSearchParams(params);
    Object.entries(savedParams).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.delete("page");
    setParams(p);
  };
  const clearSaved = () => {
    const p = new URLSearchParams(params);
    ["size", "waist", "inseam"].forEach((k) => p.delete(k));
    p.delete("page");
    setParams(p);
    if (sizeCtx) autoAppliedRef.current.add(JSON.stringify(sizeCtx));
  };
  const ctxKey = sizeCtx ? JSON.stringify(sizeCtx) : "";
  useEffect(() => {
    if (!ctxKey || !savedHas || autoAppliedRef.current.has(ctxKey)) return;
    autoAppliedRef.current.add(ctxKey);
    if (["size", "waist", "inseam"].some((k) => params.get(k))) return;
    const p = new URLSearchParams(params);
    Object.entries(savedParams).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.delete("page");
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxKey, savedHas]);
  const verifiedOnly = params.get("verified_only") === "1";
  const hideReplicas = params.get("hide_replicas") === "1";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  useEffect(() => { setSearchInput(params.get("q") || ""); }, [params]);

  useEffect(() => {
    const bp = {};
    ["product_type", "category", "dept", "audience", "garment_type", "gender", "age_group", "kid_gender"].forEach((k) => {
      if (filters[k]) bp[k] = filters[k];
    });
    api.get("/brands", { params: bp }).then((r) => setBrands(r.data.filter((b) => b.count > 0).slice(0, 10))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.product_type, filters.category, filters.dept, filters.audience, filters.garment_type, filters.gender, filters.age_group, filters.kid_gender]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    if (verifiedOnly) q.verified_only = true;
    if (hideReplicas) q.hide_replicas = true;
    const listQ = { ...q, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
    Promise.all([
      api.get("/listings", { params: listQ }),
      api.get("/listings/count", { params: q }),
    ]).then(([lr, cr]) => { setItems(lr.data); setCount(cr.data.count || 0); }).finally(() => setLoading(false));    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    api.get("/demo-status").then((r) => setDemoTotal(r.data.total_demos || 0)).catch(() => {});
  }, [params]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v) p.delete(k); else p.set(k, v);
    p.delete("page"); // any filter change returns to page 1
    setParams(p);
  };

  const setAudience = (key) => {
    const p = new URLSearchParams(params);
    if (key) p.set("audience", key); else p.delete("audience");
    p.delete("size");        // shoe-size options depend on the audience
    p.delete("gender");      // audience replaces the (hidden) gender/age filters
    p.delete("age_group");
    p.delete("page");
    setParams(p);
  };

  const setGarment = (key) => {
    const p = new URLSearchParams(params);
    if (key) p.set("garment_type", key); else p.delete("garment_type");
    p.delete("page");
    setParams(p);
  };

  const setKidGender = (key) => {
    const p = new URLSearchParams(params);
    if (key) p.set("kid_gender", key); else p.delete("kid_gender");
    p.delete("garment_type"); // garment list depends on boys/girls (boys hides dresses & skirts)
    p.delete("page");
    setParams(p);
  };

  const goPage = (n) => {
    const p = new URLSearchParams(params);
    if (n <= 1) p.delete("page"); else p.set("page", String(n));
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitSearch = (e) => { e.preventDefault(); setFilter("q", searchInput.trim() || null); };

  const [price, setPrice] = useState([Number(params.get("min_price")) || 0, Number(params.get("max_price")) || PRICE_MAX]);
  useEffect(() => {
    setPrice([Number(params.get("min_price")) || 0, Number(params.get("max_price")) || PRICE_MAX]);
  }, [params]);
  const commitPrice = (vals) => {
    const p = new URLSearchParams(params);
    if (vals[0] > 0) p.set("min_price", String(vals[0])); else p.delete("min_price");
    if (vals[1] < PRICE_MAX) p.set("max_price", String(vals[1])); else p.delete("max_price");
    p.delete("page");
    setParams(p);
  };
  const applyPreset = (pre) => {
    const p = new URLSearchParams(params);
    if (pre.min != null) p.set("min_price", String(pre.min)); else p.delete("min_price");
    if (pre.max != null) p.set("max_price", String(pre.max)); else p.delete("max_price");
    p.delete("page");
    setParams(p);
  };
  const presetActive = (pre) => {
    const min = filters.min_price ? Number(filters.min_price) : null;
    const max = filters.max_price ? Number(filters.max_price) : null;
    return min === pre.min && max === pre.max;
  };

  const buildLabel = (sp) => {
    const bits = [];
    if (sp.q) bits.push(`“${sp.q}”`);
    ["brand", "color", "material", "gender", "product_type", "era"].forEach((k) => { if (sp[k]) bits.push(sp[k]); });
    if (sp.size) bits.push(`size ${sp.size}`);
    if (sp.min_price || sp.max_price) bits.push(`$${sp.min_price || 0}–${sp.max_price || PRICE_MAX}`);
    return bits.join(" · ").slice(0, 80) || "All items";
  };
  const [saving, setSaving] = useState(false);
  const saveSearch = async () => {
    const sp = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) sp[k] = v; });
    if (verifiedOnly) sp.verified_only = "1";
    if (hideReplicas) sp.hide_replicas = "1";
    setSaving(true);
    try {
      await api.post("/saved-searches", { label: buildLabel(sp), params: sp });
      toast.success("Search saved — new matches will show in your alerts (bell & Saved › Searches)");
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Sign in to save searches" : "Could not save this search");
    } finally { setSaving(false); }
  };

  const pageWindow = () => {
    const w = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i++) w.push(i);
    return w;
  };

  const seoLabel = filters.q ? `“${filters.q}” — Search`
    : isFabric ? "Fabric Archive"
    : isHomeDecor ? (filters.product_type ? (HOME_DECOR_CATEGORY_LABELS[filters.product_type] || cap(filters.product_type)) : "Home Decor")
    : isShoes ? `${audience ? SHOE_AUDIENCE_LABELS[audience] + " " : ""}Shoes`
    : isApparelDept ? (DEPT_LABELS[dept] || "Shop")
    : filters.brand ? filters.brand
    : filters.product_type ? cap(filters.product_type)
    : "Shop All";
  useSeo({
    title: `${seoLabel} | Your Classic Style`,
    description: `Shop ${seoLabel.replace(/ — Search$/, "").toLowerCase()} — curated classic & vintage apparel, footwear and accessories on Your Classic Style.`,
  });

  return (
    <div data-testid="browse-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
      <Breadcrumb className="mb-5" items={filters.q
        ? [{ label: "Home", to: "/" }, { label: "Browse", to: "/browse" }, { label: `“${filters.q}”` }]
        : [{ label: "Home", to: "/" }, { label: isFabric ? "Archive" : isHomeDecor ? "Home Decor" : isShoes ? "Shoes" : isApparelDept ? DEPT_LABELS[dept] : (filters.product_type ? cap(filters.product_type) : "Browse") }]} />
      <div className="hairline-b pb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="label-eyebrow">{isFabric ? <CategoryName /> : isHomeDecor ? "Home Decor" : isShoes ? "Shoes" : isApparelDept ? DEPT_LABELS[dept] : (filters.product_type && !isFabric) ? cap(filters.product_type) : "The Marketplace"}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
            {filters.q ? `“${filters.q}”.`
              : isFabric ? (FABRIC_TYPE_LABELS[filters.product_type] ? `${FABRIC_TYPE_LABELS[filters.product_type]}.` : "The Archive.")
              : isHomeDecor ? (filters.garment_type ? `${HOME_DECOR_ITEM_LABELS[filters.garment_type] || cap(filters.garment_type)}.` : filters.product_type ? `${HOME_DECOR_CATEGORY_LABELS[filters.product_type] || cap(filters.product_type)}.` : "Home Decor.")
              : filters.brand ? `${filters.brand}.`
              : isShoes ? `${audience ? SHOE_AUDIENCE_LABELS[audience] + " " : ""}Shoes.`
              : isApparelDept ? `${filters.garment_type ? garmentLabel(dept, filters.garment_type) : deptClothingLabel}.`
              : (filters.product_type && !isFabric) ? `${cap(filters.product_type)}.`
              : filters.condition === "new" ? "New Arrivals."
              : filters.condition === "used" ? "Just Listed." : "Browse."}
          </h1>
          {!loading && (
            <div className="mt-2 text-sm text-neutral-500" data-testid="result-count">
              {count} item{count === 1 ? "" : "s"}{filters.q ? ` matching “${filters.q}”` : ""}
            </div>
          )}
          {filters.brand && (
            <button className="mt-3 chip" data-testid="clear-brand" onClick={() => setFilter("brand", null)}>
              Clear brand filter
            </button>
          )}
          {filters.seller_id && (
            <div className="mt-3 flex items-center gap-2">
              <span className="chip" data-active="true" data-testid="seller-filter-chip">
                {items[0]?.seller_name ? `${items[0].seller_name}'s storefront` : "Single seller"}
              </span>
              <button className="chip" data-testid="clear-seller" onClick={() => setFilter("seller_id", null)}>
                Clear seller filter
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col items-stretch gap-3 w-full sm:w-auto">
          <form onSubmit={submitSearch} className="flex items-center hairline bg-white" data-testid="browse-search-form" role="search">
            <Search size={16} className="ml-3 text-neutral-400 shrink-0" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              data-testid="browse-search-input" placeholder="Search items, brands, materials…"
              className="bg-transparent px-3 py-2 text-sm outline-none w-full sm:w-72" />
            {filters.q && (
              <button type="button" data-testid="browse-search-clear" onClick={() => { setSearchInput(""); setFilter("q", null); }}
                className="px-2 text-neutral-400 hover:text-black"><X size={15} /></button>
            )}
            <button type="submit" data-testid="browse-search-btn" className="btn-primary text-[0.68rem] m-1">Search</button>
          </form>
          <button type="button" onClick={saveSearch} disabled={saving} data-testid="save-search-btn"
            className="btn-outline text-[0.68rem] self-end inline-flex items-center gap-1.5">
            <BookmarkPlus size={13} /> {saving ? "Saving…" : "Save this search"}
          </button>
          <div className="flex flex-wrap gap-2">
            <button className="chip" data-active={!filters.condition} onClick={() => setFilter("condition", null)} data-testid="tab-all">{isGuided ? "View all" : "Everything"}</button>
            <button className="chip" data-active={filters.condition === "new"} onClick={() => setFilter("condition", "new")} data-testid="tab-new-arrivals">{isFabric ? "Unused" : isGuided ? "New" : "New Arrivals"}</button>
            <button className="chip" data-active={filters.condition === "used"} onClick={() => setFilter("condition", "used")} data-testid="tab-just-listed">{isFabric ? "Vintage" : isGuided ? "Classic" : "Just Listed"}</button>
          </div>
        </div>
      </div>

      {showNudge && (
        <div className="mt-6 flex items-center gap-3 hairline p-4" data-testid="size-nudge"
          style={{ borderColor: "#2563EB", background: "#EEF3FF" }}>
          <Ruler size={16} style={{ color: "#2563EB" }} className="shrink-0" />
          <p className="text-sm text-neutral-700 flex-1">
            Shop faster — <strong>save your sizes</strong> once and we'll filter {isShoes ? "shoes" : "clothing"} to your fit every visit.
          </p>
          <Link to="/my-sizes" onClick={dismissNudge} className="btn-klein text-[0.62rem]" data-testid="size-nudge-save">Save my sizes</Link>
          <button onClick={dismissNudge} data-testid="size-nudge-dismiss" aria-label="Dismiss"
            className="p-1.5 hover:bg-black/5 transition-colors"><X size={15} /></button>
        </div>
      )}

      {isShoes && (
        <div className="mt-8 hairline p-6" data-testid="shoes-guide" style={{ borderColor: "#1E3A2B" }}>
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="shoes-breadcrumb">
            <span className="inline-flex items-center gap-1.5 label-eyebrow"><Footprints size={13} /> Shoes</span>
            <ChevronRight size={12} className="text-neutral-400" />
            {audience ? (
              <button className="chip" data-active data-testid="shoe-crumb-audience" onClick={() => setAudience(null)}>
                {SHOE_AUDIENCE_LABELS[audience]} <X size={11} />
              </button>
            ) : (
              <span className="text-neutral-400 normal-case tracking-normal">Who for?</span>
            )}
            {audience && (
              <>
                <ChevronRight size={12} className="text-neutral-400" />
                <span className="text-neutral-500 normal-case tracking-normal" data-testid="shoe-crumb-condition">
                  {filters.condition === "new" ? "New" : filters.condition === "used" ? "Classic" : "View all"}
                </span>
              </>
            )}
          </div>

          {!audience ? (
            <div className="mt-5" data-testid="shoe-audience-step">
              <div className="label-eyebrow">Who are you shopping for?</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {SHOE_AUDIENCES.map((a) => (
                  <button key={a.key} data-testid={`shoe-audience-${a.key}`} onClick={() => setAudience(a.key)}
                    className="hairline p-4 text-left transition-colors hover:bg-[#F0EBE1]">
                    <div className="font-display text-lg tracking-tight">{a.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{a.note}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-5" data-testid="shoe-condition-step">
              <div className="label-eyebrow">Show me</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="chip" data-active={filters.condition === "new"} data-testid="shoe-cond-new" onClick={() => setFilter("condition", "new")}>New</button>
                <button className="chip" data-active={filters.condition === "used"} data-testid="shoe-cond-classic" onClick={() => setFilter("condition", "used")}>Classic</button>
                <button className="chip" data-active={!filters.condition} data-testid="shoe-cond-all" onClick={() => setFilter("condition", null)}>View all</button>
                <span className="w-px h-5 bg-black/10 mx-1" />
                <button className="chip" data-testid="shoe-change-audience" onClick={() => setAudience(null)}>Change shopper</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isApparelDept && (
        <div className="mt-8 hairline p-6" data-testid="apparel-guide" style={{ borderColor: "#1E3A2B" }}>
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="apparel-breadcrumb">
            <span className="label-eyebrow">{DEPT_LABELS[dept]}</span>
            {dept === "kids" && (
              <>
                <ChevronRight size={12} className="text-neutral-400" />
                {filters.kid_gender ? (
                  <button className="chip" data-active data-testid="kidgender-crumb" onClick={() => setKidGender(null)}>
                    {KID_GENDER_LABELS[filters.kid_gender]} <X size={11} />
                  </button>
                ) : (
                  <span className="text-neutral-400 normal-case tracking-normal">Boys / Girls</span>
                )}
              </>
            )}
            {(dept !== "kids" || filters.kid_gender) && (
              <>
                <ChevronRight size={12} className="text-neutral-400" />
                {filters.garment_type ? (
                  <button className="chip" data-active data-testid="garment-crumb" onClick={() => setGarment(null)}>
                    {garmentLabel(dept, filters.garment_type)} <X size={11} />
                  </button>
                ) : (
                  <span className="text-neutral-400 normal-case tracking-normal">Category</span>
                )}
              </>
            )}
            {filters.garment_type && (
              <>
                <ChevronRight size={12} className="text-neutral-400" />
                <span className="text-neutral-500 normal-case tracking-normal" data-testid="garment-crumb-condition">
                  {filters.condition === "new" ? "New" : filters.condition === "used" ? "Classic" : "View all"}
                </span>
              </>
            )}
          </div>

          {dept === "kids" && !filters.kid_gender ? (
            <div className="mt-5" data-testid="kidgender-step">
              <div className="label-eyebrow">Who are you shopping for?</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {["girls", "boys"].map((k) => (
                  <button key={k} data-testid={`kidgender-${k}`} onClick={() => setKidGender(k)}
                    className="hairline p-4 text-left transition-colors hover:bg-[#F0EBE1] font-display text-lg tracking-tight">
                    {KID_GENDER_LABELS[k]}
                  </button>
                ))}
              </div>
            </div>
          ) : !filters.garment_type ? (
            <div className="mt-5" data-testid="garment-step">
              <div className="label-eyebrow">{dept === "kids" ? `Shop ${KID_GENDER_LABELS[filters.kid_gender]}' clothing` : `Shop ${DEPT_LABELS[dept]}'s clothing`}</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {(dept === "kids" ? kidsGarments(filters.kid_gender) : (GARMENTS[dept] || [])).map((g) => (
                  <button key={g.key} data-testid={`garment-${g.key}`} onClick={() => setGarment(g.key)}
                    className="hairline p-3 text-left transition-colors hover:bg-[#F0EBE1] font-display text-base tracking-tight">
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-5" data-testid="garment-condition-step">
              <div className="label-eyebrow">Show me</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="chip" data-active={filters.condition === "new"} data-testid="garment-cond-new" onClick={() => setFilter("condition", "new")}>New</button>
                <button className="chip" data-active={filters.condition === "used"} data-testid="garment-cond-classic" onClick={() => setFilter("condition", "used")}>Classic</button>
                <button className="chip" data-active={!filters.condition} data-testid="garment-cond-all" onClick={() => setFilter("condition", null)}>View all</button>
                <span className="w-px h-5 bg-black/10 mx-1" />
                <button className="chip" data-testid="change-garment" onClick={() => setGarment(null)}>Change category</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="grid lg:grid-cols-[280px_1fr] gap-10 mt-10">
        <aside className="space-y-8">
          {isHomeDecor && (() => {
            const setHD = (pt, gt) => {
              const p = new URLSearchParams(params);
              p.set("category", "home_decor");
              if (pt) p.set("product_type", pt); else p.delete("product_type");
              if (gt) p.set("garment_type", gt); else p.delete("garment_type");
              p.delete("page");
              setParams(p);
            };
            return (
              <div className="space-y-5" data-testid="home-decor-sidebar">
                <button className="chip justify-start w-full" data-active={!filters.product_type}
                  onClick={() => setHD(null, null)} data-testid="hd-all">All home decor</button>
                {HOME_DECOR_CATEGORIES.map((cat) => (
                  <div key={cat} data-testid={`hd-cat-${cat}`}>
                    <button
                      className="label-eyebrow mb-2 hover:text-black/60 transition-colors"
                      style={filters.product_type === cat ? { color: "#1E3A2B" } : {}}
                      onClick={() => setHD(cat, null)} data-testid={`hd-cat-btn-${cat}`}>
                      {HOME_DECOR_CATEGORY_LABELS[cat]}
                    </button>
                    <div className="flex flex-col gap-1">
                      {HOME_DECOR_ITEMS[cat].map(([k, l]) => (
                        <button key={k} className="chip justify-start"
                          data-active={filters.garment_type === k}
                          onClick={() => setHD(cat, filters.garment_type === k ? null : k)}
                          data-testid={`hd-item-${k}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          {!isFabric && !isHomeDecor && (
          <div className="hairline p-4" style={{ borderColor: "#2563EB" }}>
            <div className="label-eyebrow" style={{ color: "#2563EB" }}>Authenticity</div>
            <div className="mt-3 flex flex-col gap-2">
              <button className="chip justify-start" data-active={verifiedOnly} data-testid="filter-verified-only"
                onClick={() => setFilter("verified_only", verifiedOnly ? null : "1")}>
                <ShieldCheck size={12} /> Verified authentic only
              </button>
              <button className="chip justify-start" data-active={hideReplicas} data-testid="filter-hide-replicas"
                onClick={() => setFilter("hide_replicas", hideReplicas ? null : "1")}>
                <Tag size={12} /> Hide disclosed replicas
              </button>
            </div>
          </div>
          )}
          {!isPattern && !isWatches && !isHomeDecor && <FilterRow label="Color" options={COLORS} value={filters.color} onChange={(v) => setFilter("color", v)} testidPrefix="filter-color" />}
          {isPattern && (() => {
            const setP = (patch) => { const p = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k)); p.delete("page"); setParams(p); };
            const wearer = (g, a, kg) => () => setP({ gender: g, age_group: a, kid_gender: kg, garment_type: "" });
            const activeW = (a, g) => filters.age_group === a && (g === undefined || filters.gender === g);
            const NUM = ["2","4","6","8","10","12","14","16","18","20","22","24"];
            const LET = ["XS","S","M","L","XL","XXL"];
            const CATS = [["curtains","Curtains"],["linens","Linens"],["tablecloths","Tablecloths"],["tote_bags","Tote bags"],["other_pattern","Other patterns"]];
            const DIFF = [["beginner","Beginner"],["intermediate","Intermediate"],["advanced","Advanced"]];
            const CLOTH = [["dresses","Dresses"],["tops","Tops & blouses"],["shirts","Shirts"],["skirts","Skirts"],["trousers","Trousers & pants"],["shorts","Shorts"],["jackets","Jackets & coats"],["jumpsuits","Jumpsuits & rompers"],["nightwear","Nightwear"],["activewear","Activewear"],["swimwear","Swimwear"]];
            const chip = (label, active, onClick, tid) => (
              <button key={label} onClick={onClick} data-testid={tid}
                className="chip" data-active={active ? "true" : "false"}>{label}</button>);
            return (
              <div className="space-y-5" data-testid="pattern-sidebar">
                <div>
                  <div className="label-eyebrow mb-2">Search patterns</div>
                  <input key={filters.q || "empty"} defaultValue={filters.q || ""} data-testid="pattern-search-input"
                    placeholder="Keyword, size, sex, age, category…"
                    onKeyDown={(e) => { if (e.key === "Enter") setP({ q: e.currentTarget.value }); }}
                    className="w-full hairline p-2 text-sm bg-white" />
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Who it's for</div>
                  <div className="flex flex-wrap gap-2">
                    {chip("Women's", activeW("adult","women"), wearer("women","adult",""), "pat-wearer-womens")}
                    {chip("Men's", activeW("adult","men"), wearer("men","adult",""), "pat-wearer-mens")}
                    {chip("Teens", activeW("teen"), wearer("","teen",""), "pat-wearer-teens")}
                    {chip("Kids", activeW("kid"), wearer("","kid",""), "pat-wearer-kids")}
                    {chip("Babies", activeW("infant"), wearer("","infant",""), "pat-wearer-babies")}
                  </div>
                  {["teen", "kid", "infant"].includes(filters.age_group) && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="pattern-sex">
                      {chip("Girl", filters.kid_gender === "girls", () => setP({ kid_gender: "girls" }), "pat-sex-girl")}
                      {chip("Boy", filters.kid_gender === "boys", () => setP({ kid_gender: "boys" }), "pat-sex-boy")}
                    </div>
                  )}
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Size</div>
                  <div className="flex flex-wrap gap-2">
                    {LET.map((s) => chip(s, filters.size === s, () => setP({ size: filters.size === s ? "" : s }), `pat-size-${s}`))}
                    {NUM.map((s) => chip(s, filters.size === s, () => setP({ size: filters.size === s ? "" : s }), `pat-size-${s}`))}
                  </div>
                </div>
                <div data-testid="pattern-measure">
                  <div className="label-eyebrow mb-2">Finished size (width & length)</div>
                  <div className="flex gap-2">
                    <input value={patW} onChange={(e) => setPatW(e.target.value)} placeholder="Width"
                      className="w-full hairline p-2 text-sm bg-white" data-testid="pattern-filter-width" />
                    <input value={patL} onChange={(e) => setPatL(e.target.value)} placeholder="Length"
                      className="w-full hairline p-2 text-sm bg-white" data-testid="pattern-filter-length" />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setP({ pattern_width: patW.trim(), pattern_length: patL.trim() })}
                      className="btn-klein text-[0.6rem] flex-1" data-testid="pattern-filter-apply">Search</button>
                    <button onClick={() => { setPatW(""); setPatL(""); setP({ pattern_width: "", pattern_length: "" }); }}
                      className="btn-outline text-[0.6rem] flex-1" data-testid="pattern-filter-all">All</button>
                  </div>
                  <p className="text-[11px] text-neutral-400 mt-1.5">Great for men's patterns — search by chest width or garment length.</p>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Clothing category</div>
                  <div className="flex flex-wrap gap-2" data-testid="pattern-clothing">
                    {CLOTH.map(([k, l]) => chip(l, filters.garment_type === k,
                      () => setP({ garment_type: filters.garment_type === k ? "" : k }), `pat-cloth-${k}`))}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Pattern category</div>
                  <div className="flex flex-wrap gap-2">
                    {CATS.map(([k, l]) => chip(l, filters.garment_type === k,
                      () => setP({ garment_type: filters.garment_type === k ? "" : k, gender: "", age_group: "", kid_gender: "" }), `pat-cat-${k}`))}
                  </div>
                </div>
                {brands.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">Pattern brand</div>
                    <div className="flex flex-wrap gap-2" data-testid="pattern-brand">
                      {chip("All", !filters.brand, () => setP({ brand: "" }), "pat-brand-all")}
                      {brands.map((b) => chip(`${b.name} ${b.count}`, filters.brand === b.name,
                        () => setP({ brand: filters.brand === b.name ? "" : b.name }), `pat-brand-${b.name}`))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="label-eyebrow mb-2">Difficulty</div>
                  <div className="flex flex-wrap gap-2" data-testid="pattern-difficulty">
                    {DIFF.map(([k, l]) => chip(l, filters.difficulty === k,
                      () => setP({ difficulty: filters.difficulty === k ? "" : k }), `pat-diff-${k}`))}
                  </div>
                </div>
                <button className="chip" data-testid="pattern-clear"
                  onClick={() => { setPatW(""); setPatL(""); setP({ gender: "", age_group: "", kid_gender: "", size: "", garment_type: "", q: "", brand: "", difficulty: "", pattern_width: "", pattern_length: "" }); }}>Clear all</button>
              </div>
            );
          })()}
          <div data-testid="filter-price">
            <div className="label-eyebrow mb-3 flex items-center justify-between">
              <span>Price</span>
              <span className="normal-case tracking-normal text-neutral-600" data-testid="price-readout">
                ${price[0]}{price[1] >= PRICE_MAX ? "+" : `–$${price[1]}`}
              </span>
            </div>
            <Slider min={0} max={PRICE_MAX} step={5} value={price}
              onValueChange={setPrice} onValueCommit={commitPrice}
              data-testid="price-slider" className="mt-1" />
            <div className="mt-3 flex flex-wrap gap-2" data-testid="price-presets">
              {PRICE_PRESETS.map((pre) => (
                <button key={pre.key} className="chip" data-active={presetActive(pre)}
                  data-testid={`price-preset-${pre.key}`}
                  onClick={() => (presetActive(pre)
                    ? (() => { const p = new URLSearchParams(params); p.delete("min_price"); p.delete("max_price"); p.delete("page"); setParams(p); })()
                    : applyPreset(pre))}>
                  {pre.label}
                </button>
              ))}
            </div>
            {(filters.min_price || filters.max_price) && (
              <button className="chip mt-3" data-testid="clear-price"
                onClick={() => { const p = new URLSearchParams(params); p.delete("min_price"); p.delete("max_price"); p.delete("page"); setParams(p); }}>
                Clear price
              </button>
            )}
          </div>
          {isShoes ? (
            audience ? (
              <FilterRow label="Shoe size (US)" options={SHOE_SIZES[audience] || []} value={filters.size}
                onChange={(v) => setFilter("size", v)} testidPrefix="filter-shoe-size" />
            ) : (
              <div data-testid="shoe-size-locked">
                <div className="label-eyebrow mb-2">Shoe size</div>
                <p className="text-xs text-neutral-500">Pick who you're shopping for above to filter by size.</p>
              </div>
            )
          ) : isApparelDept ? (
            filters.garment_type ? (
              usesWaistLength(dept, filters.garment_type) ? (
                <div className="space-y-5" data-testid="waist-length-filters">
                  <FilterRow label="Waist" options={WAIST_SIZES[dept] || []} value={filters.waist}
                    onChange={(v) => setFilter("waist", v)} testidPrefix="filter-waist" />
                  <FilterRow label="Length (inseam)" options={INSEAM_SIZES[dept] || []} value={filters.inseam}
                    onChange={(v) => setFilter("inseam", v)} testidPrefix="filter-inseam" />
                </div>
              ) : (
                <FilterRow label="Size" options={CLOTHING_SIZES[dept] || []} value={filters.size}
                  onChange={(v) => setFilter("size", v)} testidPrefix="filter-clothing-size" />
              )
            ) : (
              <div data-testid="clothing-size-locked">
                <div className="label-eyebrow mb-2">Size</div>
                <p className="text-xs text-neutral-500">Pick a category above to filter by size.</p>
              </div>
            )
          ) : ACCESSORY_SIZES[filters.product_type] ? (
            <FilterRow label={ACCESSORY_SIZES[filters.product_type].label} options={ACCESSORY_SIZES[filters.product_type].options}
              value={filters.size} onChange={(v) => setFilter("size", v)} testidPrefix="filter-acc-size" />
          ) : SIZELESS.includes(filters.product_type) ? null : isPattern ? null : isHomeDecor ? null : isFabric ? (
            <div data-testid="filter-fabric-size">
              <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 mb-2">Measurements</div>
              <div className="flex gap-2">
                <input value={fabW} onChange={(e) => setFabW(e.target.value)} placeholder="Width"
                  className="w-full hairline p-2 text-sm bg-white" data-testid="fabric-filter-width" />
                <input value={fabL} onChange={(e) => setFabL(e.target.value)} placeholder="Length"
                  className="w-full hairline p-2 text-sm bg-white" data-testid="fabric-filter-length" />
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setFilter("size", [fabW.trim(), fabL.trim()].filter(Boolean).join(" "))}
                  className="btn-klein text-[0.6rem] flex-1" data-testid="fabric-filter-apply">Search</button>
                <button onClick={() => { setFabW(""); setFabL(""); setFilter("size", ""); }}
                  className="btn-outline text-[0.6rem] flex-1" data-testid="fabric-filter-all">All</button>
              </div>
              <p className="text-[11px] text-neutral-400 mt-1.5">Type any number, e.g. 44 or 2. Leave blank for all.</p>
            </div>
          ) : (
            <FilterRow label="Size" options={SIZES} value={filters.size} onChange={(v) => setFilter("size", v)} testidPrefix="filter-size" />
          )}
          {sizeCtx && savedHas && (
            savedActive ? (
              <button className="chip" data-active data-testid="clear-my-size" onClick={clearSaved}>
                <Ruler size={12} /> Your size: {savedLabel} <X size={11} />
              </button>
            ) : (
              <button className="chip" data-testid="use-my-size" onClick={applySaved}>
                <Ruler size={12} /> Shop my size ({savedLabel})
              </button>
            )
          )}
          {!isFabric && !isHomeDecor && brands.length > 0 && (
            <div>
              <div className="label-eyebrow mb-3 flex items-center justify-between">
                <span>Brand</span>
                <Link to="/brands" className="normal-case tracking-normal text-[0.7rem] underline">All brands</Link>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="chip" data-active={!filters.brand} onClick={() => setFilter("brand", null)} data-testid="filter-brand-all">All</button>
                {brands.map((b) => (
                  <button key={b.name} className="chip inline-flex items-center gap-1" data-active={filters.brand === b.name}
                    onClick={() => setFilter("brand", b.name)} data-testid={`filter-brand-${b.name}`}>
                    {b.heritage && <Crown size={11} style={{ color: "#C99A2E" }} data-testid={`heritage-mark-${b.name}`} aria-label="Heritage house" />}
                    {b.name} <span className="opacity-50">{b.count}</span>
                  </button>
                ))}
              </div>
              {filters.brand && <div className="mt-3"><BrandFollowButton brand={filters.brand} className="text-[0.7rem]" /></div>}
            </div>
          )}
          {isWatches && (
            <>
              <FilterRow label="Band" options={WATCH_BANDS} labels={WATCH_BAND_LABELS} value={filters.band} onChange={(v) => setFilter("band", v)} testidPrefix="filter-band" />
              <FilterRow label="Movement" options={WATCH_MOVEMENTS} labels={WATCH_MOVEMENT_LABELS} value={filters.movement} onChange={(v) => setFilter("movement", v)} testidPrefix="filter-movement" />
              <FilterRow label="Dial colour" options={WATCH_DIAL_COLORS} value={filters.dial_color} onChange={(v) => setFilter("dial_color", v)} testidPrefix="filter-dial" />
              <FilterRow label="Water resistance" options={WATCH_WATER_RESISTANCE} value={filters.water_resistance} onChange={(v) => setFilter("water_resistance", v)} testidPrefix="filter-wr" />
            </>
          )}
          {isJewelry && (
            <>
              <FilterRow label="Metal" options={JEWELRY_METALS} labels={JEWELRY_METAL_LABELS} value={filters.metal} onChange={(v) => setFilter("metal", v)} testidPrefix="filter-metal" />
              <FilterRow label="Hallmark / purity" options={JEWELRY_HALLMARKS} labels={JEWELRY_HALLMARK_LABELS} value={filters.hallmark} onChange={(v) => setFilter("hallmark", v)} testidPrefix="filter-hallmark" />
              <FilterRow label="Stone" options={JEWELRY_STONES} labels={JEWELRY_STONE_LABELS} value={filters.stone} onChange={(v) => setFilter("stone", v)} testidPrefix="filter-stone" />
              {filters.product_type === "rings" && (
                <div data-testid="filter-resizable-row">
                  <div className="label-eyebrow mb-2">Resizing</div>
                  <button className="chip inline-flex items-center gap-1.5" data-active={filters.resizable === "true"}
                    data-testid="filter-resizable"
                    onClick={() => setFilter("resizable", filters.resizable === "true" ? null : "true")}>
                    <Ruler size={12} /> Resizable only
                  </button>
                </div>
              )}
            </>
          )}
          {isWallets && (
            <FilterRow label="Wallet style" options={WALLET_STYLES} labels={WALLET_STYLE_LABELS} value={filters.garment_type} onChange={(v) => setFilter("garment_type", v)} testidPrefix="filter-wstyle" />
          )}
          {isWallets && (
            <FilterRow label="Card slots" options={WALLET_CARD_SLOTS} value={filters.card_slots} onChange={(v) => setFilter("card_slots", v)} testidPrefix="filter-cardslots" />
          )}
          {isHandbags && (
            <>
              <FilterRow label="Bag style" options={HANDBAG_STYLES} labels={HANDBAG_STYLE_LABELS} value={filters.garment_type} onChange={(v) => setFilter("garment_type", v)} testidPrefix="filter-bagstyle" />
              <FilterRow label="Bag size" options={HANDBAG_SIZES} labels={HANDBAG_SIZE_LABELS} value={filters.size} onChange={(v) => setFilter("size", v)} testidPrefix="filter-bagsize" />
            </>
          )}
          {isTies && (
            <FilterRow label="Tie style" options={TIE_STYLES} labels={TIE_STYLE_LABELS} value={filters.garment_type} onChange={(v) => setFilter("garment_type", v)} testidPrefix="filter-tiestyle" />
          )}
          {!isFabric && !isWatches && !isNecklaces && !isHomeDecor && <FilterRow label="Material" options={MATERIALS} value={filters.material} onChange={(v) => setFilter("material", v)} testidPrefix="filter-material" />}
          {!isPattern && !isHomeDecor && <FilterRow label="Era" options={ERAS} labels={ERA_LABELS} value={filters.era} onChange={(v) => setFilter("era", v)} testidPrefix="filter-era" />}
          {!isFabric && !isGuided && !isHandbags && !isHomeDecor && <FilterRow label="Gender" options={GENDERS} value={filters.gender} onChange={(v) => setFilter("gender", v)} testidPrefix="filter-gender" />}
          {!isFabric && !isGuided && !isHandbags && !isWatches && !isTies && !isHomeDecor && <FilterRow label="Age" options={AGES} value={filters.age_group} onChange={(v) => setFilter("age_group", v)} testidPrefix="filter-age" />}
          {!isGuided && !isHandbags && !isPattern && !isWatches && !isHats && !isNecklaces && !isWallets && !isTies && !isHomeDecor && (
            <FilterRow label={isFabric ? "Fabric type" : "Type"} options={isFabric ? FABRIC_TYPES : PRODUCT_TYPES}
              labels={isFabric ? FABRIC_TYPE_LABELS : null} value={filters.product_type}
              onChange={(v) => setFilter("product_type", v)} testidPrefix="filter-type" />
          )}
          {!isGuided && !isPattern && !isWatches && !isNecklaces && !isWallets && !isHandbags && !isHomeDecor && <FilterRow label="Pattern" options={PATTERNS} value={filters.pattern} onChange={(v) => setFilter("pattern", v)} testidPrefix="filter-pattern" />}
          {!isFabric && !isWallets && !isHandbags && !isTies && !isHomeDecor && <FilterRow label="Style" options={STYLES} value={filters.style} onChange={(v) => setFilter("style", v)} testidPrefix="filter-style" />}
        </aside>
        <div>
          {loading ? (
            <div className="text-neutral-500 label-eyebrow">Loading…</div>
          ) : items.length === 0 ? (
            <div data-testid="empty-state" className="hairline p-16 text-center">
              <div className="label-eyebrow">Nothing here yet</div>
              <p className="mt-3 text-neutral-500">
                {filters.q ? `No items match “${filters.q}”. Try a different search or clear your filters.`
                  : <>Be the first to list something for this filter — head to <span className="text-black">Sell</span>.</>}
              </p>
            </div>
          ) : (
            <>
              {demoTotal > 0 && items.length > 0 && items.every((i) => !i.demo_sold_out) && (
                <div data-testid="real-finds-banner"
                  className="mb-5 flex items-center gap-2.5 hairline px-4 py-3 text-xs uppercase tracking-[0.16em]"
                  style={{ borderColor: "#1E3A2B", color: "#1E3A2B", background: "#F3F6F2" }}>
                  <ShieldCheck size={15} strokeWidth={1.8} />
                  Only real finds left — every piece here is a genuine seller listing.
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {items.map((it) => <ProductCard key={it.id} item={it} />)}
              </div>
              {totalPages > 1 && (
                <nav className="mt-10 flex items-center justify-center gap-1.5" data-testid="pagination">
                  <button className="chip" data-testid="page-prev" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                    <ChevronLeft size={14} /> Prev
                  </button>
                  {pageWindow()[0] > 1 && (
                    <>
                      <button className="chip" data-testid="page-1" onClick={() => goPage(1)}>1</button>
                      {pageWindow()[0] > 2 && <span className="px-1 text-neutral-400">…</span>}
                    </>
                  )}
                  {pageWindow().map((n) => (
                    <button key={n} className="chip" data-active={n === page} data-testid={`page-${n}`} onClick={() => goPage(n)}>{n}</button>
                  ))}
                  {pageWindow().slice(-1)[0] < totalPages && (
                    <>
                      {pageWindow().slice(-1)[0] < totalPages - 1 && <span className="px-1 text-neutral-400">…</span>}
                      <button className="chip" data-testid={`page-${totalPages}`} onClick={() => goPage(totalPages)}>{totalPages}</button>
                    </>
                  )}
                  <button className="chip" data-testid="page-next" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>
                    Next <ChevronRight size={14} />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
      <RecentlyViewed className="!px-0 !max-w-none mt-16" />
    </div>
  );
}
